import { differenceInDays } from 'date-fns';
import {
  EventDateLocation,
  EventTitle,
  EventTopics,
  LearnMoreButton,
  RegisterNowButton,
} from '@/app_components/Conferences/EventInfo';
import { DescriptionParser } from '@/app_lib/htmlParser/DescriptionParser';
import type { ConferencesEvent, EventsListEvent } from '@/app_lib/types/Conferences';
import { cn } from '@/app_lib/utils/utils';

const contentLayoutClass = 'flex flex-1 flex-col layout-gap-xs justify-between min-h-0 min-w-0';

const tileCtaButtonClass = 'min-w-0 flex-1 basis-0 max-lg:w-auto';

const tileCtaRowClass =
  'flex min-w-0 w-full shrink-0 flex-nowrap gap-lg justify-center lg:justify-start transition-none';

export const EventInfoSecondary = ({
  event,
  past = false,
}: {
  event: EventsListEvent | ConferencesEvent;
  past?: boolean;
}) => {
  const daysAway = differenceInDays(new Date(event.endDate), new Date());
  const hasHoverState = !past && daysAway >= 0;

  const topBlock = ({ className }: { className?: string }) => (
    <>
      <EventDateLocation
        event={event}
        type='secondary'
      />
      <div className='flex min-h-0 min-w-0 flex-col gap-sm'>
        <EventTopics event={event} />
        <EventTitle
          className={cn('line-clamp-2 md:line-clamp-3', className)}
          event={event}
        />
      </div>
    </>
  );

  const defaultButtons = (size: 'md' | 'lg' = 'md') => (
    <div className={tileCtaRowClass}>
      <LearnMoreButton
        className={tileCtaButtonClass}
        externalLink={event.externalLink}
        size={size}
        type='tile'
        variant='secondaryOutline'
      />
      {!past && (
        <RegisterNowButton
          className={tileCtaButtonClass}
          registerLink={event.registerLink}
          size={size}
          type='tile'
          variant='secondary'
        />
      )}
    </div>
  );

  const hoverButtons = (size: 'md' | 'lg' = 'md') => (
    <div className={tileCtaRowClass}>
      <LearnMoreButton
        className={cn(tileCtaButtonClass, 'hover:text-black! active:text-black!')}
        externalLink={event.externalLink}
        size={size}
        type='tile'
        variant='whiteOutline'
      />
      {!past && (
        <RegisterNowButton
          className={cn(tileCtaButtonClass, 'text-black! hover:text-white! active:text-white!')}
          registerLink={event.registerLink}
          size={size}
          type='tile'
          variant='secondarySpecialBlue'
        />
      )}
    </div>
  );

  if (!hasHoverState) {
    return (
      <div className='flex min-h-0 min-w-0 w-full flex-1 flex-col'>
        <div className={contentLayoutClass}>
          {topBlock({ className: 'xl:h-[9rem] lg:h-[8rem] md:h-[6.5rem] h-[4.3rem]' })}
          <div className='min-w-0 shrink-0'>
            <div className='lg:max-xl:hidden'>{defaultButtons('lg')}</div>
            <div className='lg:max-xl:block hidden'>{defaultButtons('md')}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    // The idea here is that we have two states: default and hover. To prevent CLS, we pretty much swap between the two states
    <div className='relative flex min-h-0 min-w-0 w-full flex-1 flex-col'>
      {/* Default state: title lower, no description, default buttons — visible when not hovered */}
      <div className={cn(contentLayoutClass, 'opacity-100 group-hover:opacity-0 group-data-[active=true]:opacity-0')}>
        {topBlock({ className: 'xl:h-[9rem] lg:h-[8rem] md:h-[6.5rem] h-[4.3rem]' })}
        <div className='min-w-0 shrink-0'>
          <div className='lg:max-xl:hidden'>{defaultButtons('lg')}</div>
          <div className='lg:max-xl:block hidden'>{defaultButtons('md')}</div>
        </div>
      </div>
      {/* Hover state: title higher, description, hover buttons — visible when hovered; no layout reflow */}
      <div
        className={cn(
          contentLayoutClass,
          'absolute inset-0 opacity-0 group-hover:opacity-100 group-data-[active=true]:opacity-100',
        )}
      >
        {topBlock({ className: event.shortDescription ? '' : 'xl:h-[9rem] lg:h-[8rem] md:h-[6.5rem] h-[4.3rem]' })}
        {event.shortDescription && (
          <DescriptionParser
            className='text-secondary line-clamp-2 md:line-clamp-3'
            content={event.shortDescription}
          />
        )}
        <div className='min-w-0 shrink-0'>
          <div className='lg:max-xl:hidden'>{hoverButtons('lg')}</div>
          <div className='lg:max-xl:block hidden'>{hoverButtons('md')}</div>
        </div>
      </div>
    </div>
  );
};
